import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormGroup, UntypedFormBuilder } from '@angular/forms';
import { RefDataService } from '@services/ref-data.service';
import { APP_CONFIG, ConfigService } from '@services/config.service';
import { BinariesService } from '@services/binaries/binaries.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
  ApplianceGroup,
  DataRefsByKey,
  Domain,
  Language,
  LanguagesByKey,
  LocalizedKeyValue,
  Market,
  Unit,
} from '@shared/models/dataref.model';
import { MatSelectChange } from '@angular/material/select';
import { RecipeForm } from '@shared/models/recipe/recipe-form.model';
import { BINARY_TARGET_DOMAIN } from '@packs/utils/pack.utils';

@Component({
  selector: 'app-recipes-form-new',
  templateUrl: './recipes-form-new.component.html',
  styleUrls: ['./recipes-form-new.component.scss'],
  standalone: false,
})
export class RecipesFormNewComponent implements OnChanges, OnInit, OnDestroy {
  @Input() formRecipe: FormGroup<RecipeForm>;
  @Input() yieldUnits: DataRefsByKey<Unit> = {};
  @Output() save = new EventEmitter();
  domainsList: Domain[] = [];
  marketsList: Market[] = [];
  languagesList: Language[] = [];
  filteredYieldUnits: Unit[] = [];
  courseTypes: LocalizedKeyValue[] = [];
  isPremium = false;
  domainsObject = null;
  lang = 'en';

  public applianceGroupsList$: BehaviorSubject<ApplianceGroup[]> = new BehaviorSubject<ApplianceGroup[]>([]);
  private readonly destroy$: Subject<void> = new Subject();

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly configService: ConfigService,
    private readonly binariesService: BinariesService,
    private readonly refDataService: RefDataService,
  ) {}

  ngOnInit(): void {
    this.lang = this.configService.currentLanguage;

    this.refDataService
      .getByKey('domains', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allDomains) => {
        this.domainsList = this.refDataService.translateReference(this.refDataService.toArray(allDomains));
        this.domainsObject = allDomains;
        this.checkDisabledInput();
        this.initNewForm();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.yieldUnits?.currentValue) {
      this.filteredYieldUnits = this.refDataService.alphabeticOrder(
        this.refDataService.toArray(this.yieldUnits).filter((unit) => unit.type?.key === 'QUANTITY'),
        this.lang,
      );
    }
  }

  initNewForm(): void {
    /*
     * TODO: We should have a separate form for create and edit as they don't share the same purpose
     *
     * This component should not be responsible for deactivating some controls because they have validator on them..
     * => It introduce a high coupling between parent and child as we must know which field has a validator to disable
     */

    // disable input form when create new recipe
    this.formRecipe.controls.ingredients.disable();
    this.formRecipe.controls.resourceMedias.disable();
    this.formRecipe.controls.steps.disable();
    this.formRecipe.controls.durations.disable();

    this.formRecipe.patchValue({
      status: 'PRIVATE',
      privacyLevel: 'PUBLIC',
      recipeType: 'BRAND',
      isPremium: false,
    });
  }

  selectMarket(marketKey: string): void {
    this.formRecipe.patchValue({
      lang: null,
      title: '',
      shortTitle: '',
      applianceGroups: [],
      courses: [],
      market: marketKey,
    });
    const market = this.marketsList.find((marketToSearchFor: Market) => marketKey === marketToSearchFor.key);

    let list = market ? this.refDataService.toArray(market.langs as LanguagesByKey) : [];
    list = list?.map((data: LocalizedKeyValue) => {
      const tr = this.refDataService.innerTranslate(data.name);
      return { ...data, translatedName: tr[this.configService.currentLanguage] ?? tr.en };
    });
    this.languagesList = list;
    if (this.languagesList.length === 1) {
      this.formRecipe.controls.lang.setValue(this.languagesList[0].key);
      this.selectLanguage(this.languagesList[0]);
    }
    this.checkDisabledInput();
  }

  selectDomain(domainChangeEvt: MatSelectChange): void {
    const domainKey: string = domainChangeEvt.value;
    this.formRecipe.patchValue({
      market: null,
      lang: null,
      title: '',
      shortTitle: '',
      isPremium: false,
      applianceGroups: [],
      courses: [],
      domain: domainKey,
    });
    this.applianceGroupsList$.next([]);
    this.refDataService
      .getByKey('markets', true, domainKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allMarkets: DataRefsByKey<Market>) => {
        this.marketsList = this.refDataService.translateReference(this.refDataService.toArray(allMarkets));
      });
    this.checkDisabledInput();
  }

  selectLanguage(language: Language): void {
    const lang = language.key;
    this.formRecipe.patchValue({
      applianceGroups: [],
      lang,
    });

    const domain = this.formRecipe.controls.domain.value;
    const market = this.formRecipe.controls.market.value;

    if (domain === null || market === null) {
      return;
    }
    this.refDataService
      .getByKey('applianceGroups', true, domain, market, lang)
      .pipe(
        map((untypedApplianceGroups) => untypedApplianceGroups as DataRefsByKey<ApplianceGroup>),
        takeUntil(this.destroy$),
      )
      .subscribe((allApplianceGroups) => {
        const applianceGroupsList = this.refDataService.toArray<ApplianceGroup>(allApplianceGroups);
        applianceGroupsList.forEach((applianceGroup) => {
          applianceGroup.translatedName = market && lang && applianceGroup.name[market] ? applianceGroup.name[market][lang] : '';

          if (applianceGroup.translatedName === '') {
            applianceGroup.translatedName = applianceGroup.name.GS_INT?.en || applianceGroup.key;
          }
        });

        this.applianceGroupsList$.next(applianceGroupsList);
      });
    this.refDataService
      .getByKey('courseTypes', true, domain, market, lang)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allCourseTypes) => {
        this.courseTypes = this.refDataService.toArray(allCourseTypes);
      });
    this.checkDisabledInput();
  }

  checkDisabledInput(): void {
    if (this.formRecipe.controls.lang.value) {
      this.formRecipe.controls.shortTitle.enable();
      this.formRecipe.controls.title.enable();
      this.formRecipe.controls.courses.enable();
      this.formRecipe.controls.yieldQuantity.enable();
      this.formRecipe.controls.yieldUnit.enable();
      this.formRecipe.controls.defaultApplianceGroup.enable();
      this.formRecipe.controls.isPremium.enable();
    } else {
      this.formRecipe.controls.shortTitle.disable();
      this.formRecipe.controls.title.disable();
      this.formRecipe.controls.courses.disable();
      this.formRecipe.controls.yieldQuantity.disable();
      this.formRecipe.controls.yieldUnit.disable();
      this.formRecipe.controls.defaultApplianceGroup.disable();
      this.formRecipe.controls.isPremium.disable();
    }
  }

  bineratorLenghtTestOnFocus(propertyName: 'title' | 'shortTitle'): void {
    // call when lost focus
    this.bineratorFieldLengthValid(propertyName);
  }

  bineratorLenghtTest(propertyName: 'title' | 'shortTitle'): void {
    this.bineratorFieldLengthValid(propertyName);
  }

  bineratorFieldLengthValid(propertyName: 'title' | 'shortTitle'): void {
    if (this.formRecipe.controls.lang.value !== 'ar') {
      return;
    }

    const currentMarketKey = this.formRecipe.controls.market.value;
    const currentLangKey = this.formRecipe.controls.lang.value;

    if (currentMarketKey === null || currentLangKey === null) {
      return;
    }
    if (this.isBineratorControlsActivated()) {
      const propertyControl: AbstractControl<string | null> = this.formRecipe.controls[propertyName];
      this.binariesService
        .isFieldLengthValid(currentMarketKey, currentLangKey, propertyName, propertyControl.value ?? '')
        .pipe(takeUntil(this.destroy$))
        .subscribe((isValid: boolean) => {
          if (isValid) {
            propertyControl.setErrors(null);
          } else {
            propertyControl.setErrors({ binerator: true });
          }
        });
    }
  }

  isBineratorControlsActivated(): boolean {
    // binerator controls should be activated for Cookeo USB/Connect & Cookeo Mini
    let hasApplianceGroupForBineratorControls = false;
    (this.formRecipe.controls.applianceGroups.value || []).forEach((applianceGroupKey: string) => {
      if (APP_CONFIG.binerator.applianceGroupsActivatedForControls.indexOf(applianceGroupKey) > -1) {
        hasApplianceGroupForBineratorControls = true;
      }
    });
    return this.formRecipe.controls.domain.value === BINARY_TARGET_DOMAIN && hasApplianceGroupForBineratorControls;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
